import { useQuery } from '@tanstack/react-query'
import { WalletStatus } from 'cosmos-kit'
import {
  ChevronDown,
  Download,
  HandCoins,
  LogOut,
  Shuffle,
  WalletIcon,
} from 'lucide-react'
import { useState } from 'react'

import { fetchDelegations } from '@/api'
import CopyWithTooltip from '@/common/CopyWithTooltip'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { formatAddress, formatAtomicCompact } from '@/helpers'
import { queryKeys } from '@/queries/keys'
import { cn } from '@/theme/utils'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'
import {
  UiDropdownMenu,
  UiDropdownMenuContent,
  UiDropdownMenuItem,
  UiDropdownMenuSeparator,
  UiDropdownMenuTrigger,
} from '@/ui/UiDropdownMenu'

import NativeDelegateModal from '../native/DelegateModal'
import NativeRedelegateModal from '../native/RedelegateModal'
import NativeUndelegateModal from '../native/UndelegateModal'

interface ConnectButtonProps {
  className?: string
}

const statusLabelMap = {
  [WalletStatus.Connecting]: 'Connecting ...',
  [WalletStatus.Rejected]: 'Reconnect',
  [WalletStatus.NotExist]: 'Install Wallet',
  [WalletStatus.Error]: 'Retry',
}

type ActiveModal = 'delegate' | 'undelegate' | 'redelegate' | null

export default function ConnectButton({ className }: ConnectButtonProps) {
  const {
    balance,
    isBalanceLoading,
    status,
    address,
    connect,
    isWalletConnected,
    username,
    disconnect,
  } = useKeplrWallet()

  const [activeModal, setActiveModal] = useState<ActiveModal>(null)

  // Check if user has any delegations
  const { data: delegationData } = useQuery({
    queryKey: queryKeys.staking.delegations(address ?? ''),
    enabled: Boolean(isWalletConnected && address),
    queryFn: async () => fetchDelegations(address!),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const hasDelegations = (delegationData?.delegation_responses ?? []).length > 0

  const onButtonClick = async () => {
    if (!isWalletConnected) {
      await connect()
    }
  }

  if (!isWalletConnected) {
    return (
      <UiButton
        variant='outline'
        className={cn(
          'w-full min-w-45 overflow-hidden rounded-lg px-3',
          'inline-flex items-center justify-center gap-2',
          className,
        )}
        onClick={onButtonClick}
        disabled={status === WalletStatus.Connecting}
      >
        <div className='flex items-center gap-2'>
          <WalletIcon />
          {statusLabelMap[status as keyof typeof statusLabelMap] ||
            'Connect Wallet'}
        </div>
      </UiButton>
    )
  }

  const shortAddress = formatAddress(address ?? '')
  const primaryLabel = username?.trim() || shortAddress

  return (
    <>
      <UiDropdownMenu>
        <UiDropdownMenuTrigger asChild>
          <UiButton
            variant='outline'
            className={cn(
              'w-full min-w-45 overflow-hidden rounded-lg px-3',
              'inline-flex items-center justify-center gap-2',
              className,
            )}
          >
            <span className='divide-border flex items-center justify-center gap-2 px-2'>
              <span className='hidden text-xs font-semibold md:visible'>
                {primaryLabel}
              </span>
              {isBalanceLoading ? (
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
              ) : null}
              {balance && (
                <span className='muted-foreground xs:max-w-[150px] truncate text-xs leading-none font-semibold md:max-w-none'>
                  {formatAtomicCompact(balance)}{' '}
                  {bridgelessChainInfo.stakeCurrency.coinDenom}
                </span>
              )}
              <ChevronDown className='h-4 w-4' />
            </span>
          </UiButton>
        </UiDropdownMenuTrigger>

        <UiDropdownMenuContent
          align='end'
          className='bg-card text-foreground w-64 rounded-lg p-2'
        >
          <div className='mb-2 flex flex-col gap-2 px-2 py-2'>
            <span className='text-sm font-semibold'>{primaryLabel}</span>
            {address && (
              <div className='bg-input flex items-center gap-2 rounded-lg px-3 py-2'>
                <span className='text-muted-foreground flex-1 truncate text-xs'>
                  {formatAddress(address, 12, 8)}
                </span>
                <CopyWithTooltip value={address} />
              </div>
            )}
            {balance && (
              <div className='flex items-center gap-2'>
                <img
                  src='branding/logo.svg'
                  className='block size-4'
                  alt='Brand Logo'
                />
                <span className='text-xs font-semibold'>
                  {formatAtomicCompact(balance)}
                </span>
                <span className='text-muted-foreground text-xs'>
                  {bridgelessChainInfo.stakeCurrency.coinDenom}
                </span>
              </div>
            )}
          </div>

          <UiDropdownMenuSeparator />

          <UiDropdownMenuItem
            className='cursor-pointer'
            onClick={() => setActiveModal('delegate')}
          >
            <HandCoins className='mr-2 h-4 w-4' />
            Delegate
          </UiDropdownMenuItem>

          <UiDropdownMenuItem
            className='cursor-pointer'
            disabled={!hasDelegations}
            onClick={() => hasDelegations && setActiveModal('undelegate')}
          >
            <Download className='mr-2 h-4 w-4' />
            Undelegate
          </UiDropdownMenuItem>

          <UiDropdownMenuItem
            className='cursor-pointer'
            disabled={!hasDelegations}
            onClick={() => hasDelegations && setActiveModal('redelegate')}
          >
            <Shuffle className='mr-2 h-4 w-4' />
            Redelegate
          </UiDropdownMenuItem>

          <UiDropdownMenuSeparator />

          <UiDropdownMenuItem
            className='cursor-pointer text-red-400 hover:bg-red-400/10 hover:text-red-400 focus:bg-red-400/10 focus:text-red-400'
            onClick={disconnect}
          >
            <LogOut className='mr-2 h-4 w-4' />
            Disconnect
          </UiDropdownMenuItem>
        </UiDropdownMenuContent>
      </UiDropdownMenu>

      <NativeDelegateModal
        open={activeModal === 'delegate'}
        onClose={() => setActiveModal(null)}
      />
      <NativeUndelegateModal
        open={activeModal === 'undelegate'}
        onClose={() => setActiveModal(null)}
      />
      <NativeRedelegateModal
        open={activeModal === 'redelegate'}
        onClose={() => setActiveModal(null)}
      />
    </>
  )
}
